import {Injectable} from '@angular/core';
import {handleResult} from "@share/utils/handle-result";
import {Result} from "@domain/common/http/result";
import {FormulaireHttpService} from "@services/admin/parametre/formulaire/formulaire-http.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ParamAvance} from "@domain/admin/parametre/formulaire/param-avance";
import {finalize} from "rxjs/operators";
import {RegleAttribution} from "@domain/od/engagements/indemnites/regle-attribution";
import {Prestation} from "@domain/prestation/prestation";

/**
 * Service du formulaire Avance
 *
 * @author Lionel Gimenez
 * @date 18/09/2025
 */
@Injectable()
export class FormulaireAvanceService {
    /** Indicateur pour savoir si on est en cours de sauvegarde */
    isSaving: boolean = false;

    /** Paramètres Avance */
    paramAvance: ParamAvance;

    /** Liste des règles d'attribution */
    listeRegleAttribution: RegleAttribution[];

    /** Liste des prestations */
    listePrestation: Prestation[];

    /** Liste des règles d'attribution option */
    listeRegleAttributionOption: { id: string,libelle: string }[];

    /** Liste des prestations option */
    listePrestationOption: { id: string,libelle: string }[];

    /** Liste des statuts */
    listeStatutOption: { id: string,libelle: string }[];

    /**
     * Constructeur
     */
    constructor(
        private formulaireHttpService: FormulaireHttpService,
        private translateService: TranslateService,
        private toastrService: ToastrService
    ) {
    }

    /**
     * Charge les données
     */
    load(): void {
        //Charge les données
        this.formulaireHttpService.getParamAvance()
            .pipe(handleResult(this.translateService, this.toastrService, null, 'global.errors.generic'))
            .subscribe((result: Result) => {
                //On récupère les données de result
                this.paramAvance = result.data.param;

                //On récupère les listes d'options
                this.listeRegleAttributionOption = this.getListeRegleAttributionOption(result.data.listeRegleAttribution);
                //Met à jour les libellés des règles d'attribution
                this.updateRegleAttributionLibelle(this.paramAvance,this.listeRegleAttributionOption)

                //On récupère les listes d'options
                this.listePrestationOption = this.getListePrestationOption(result.data.listePrestation);
                //Met à jour les libellés des prestations
                this.updatePrestationLibelle(this.paramAvance,this.listePrestationOption);

                //On récupère les listes d'options
                this.listeStatutOption = result.data.listeStatut;
                //Met à jour les libellés des statuts
                this.updateNatureLibelle(this.paramAvance,this.getListeNatureOption());
            })
    }

    /**
     * Sauvegarde les données
     */
    save(): void {
        //Indicateur de sauvegarde
        this.isSaving = true;

        //Sauvegarde
        this.formulaireHttpService.saveParamAvance(this.paramAvance)
            .pipe(
                handleResult(this.translateService, this.toastrService),
                finalize(() => this.isSaving = false))
            .subscribe((result: Result) => {
                //Refresh
                this.load();
            });
    }

    /**
     * Récupère la liste des options de règles d'attribution
     */
    private getListeRegleAttributionOption(listeRegleAttribution: RegleAttribution[]): { id: string,libelle: string }[] {
        return listeRegleAttribution.map(regleAttribution => ({
            id: regleAttribution.id.toString(),
            libelle: regleAttribution.libelle
        }));
    }

    /**
     * Récupère la liste des options de prestations
     */
    private getListePrestationOption(listePrestation: Prestation[]): { id: string,libelle: string }[] {
        return listePrestation.map(prestation => ({
            id: prestation.idPrestation.toString(),
            libelle: prestation.libelle
        }));
    }

    /**
     * Met à jour le libellé du modèle de RegleAttribution à partir des options
     */
    private updateRegleAttributionLibelle(paramAvance: ParamAvance,listeRegleAttributionOption: { id: string,libelle: string }[]): void {
        paramAvance.listeAvanceAutoFiltreIJ.forEach(avanceAutoFiltreIJ => {
            const option = listeRegleAttributionOption.find(option => option.id === avanceAutoFiltreIJ.id.toString());
            if (option) {
                avanceAutoFiltreIJ.libelle = option.libelle;
            }
        });
    }

    /**
     * Met à jour le libellé du modèle de Prestation à partir des options
     */
    private updatePrestationLibelle(paramAvance: ParamAvance,listePrestationOption: { id: string,libelle: string }[]): void {
        if (paramAvance.listeAvanceAutoFiltreFP) {
            paramAvance.listeAvanceAutoFiltreFP.forEach(avanceAutoFiltreFP => {
                const option = listePrestationOption.find(option => option.id === avanceAutoFiltreFP.id.toString());
                if (option) {
                    avanceAutoFiltreFP.libelle = option.libelle;
                }
            });
        }
    }

    /**
     * Met à jour le libellé du modèle de Nature à partir des options
     */
    private updateNatureLibelle(paramAvance: ParamAvance,listeNatureOption: { typeFact: number,libelle: string }[]): void {
        if (paramAvance.listeAvanceAutoFiltreFact) {
            paramAvance.listeAvanceAutoFiltreFact.forEach(avanceAutoFiltreFact => {
                const option = listeNatureOption.find(option => option.typeFact.toString() === avanceAutoFiltreFact.id.toString());
                if (option) {
                    avanceAutoFiltreFact.libelle = option.libelle;
                }
            });
        }
    }

    /**
     * Récupère la liste des options pour la nature
     *
     * @returns La liste des options pour la nature
     */
    getListeNatureOption(): Array<{ typeFact: number,libelle: string }> {
        //Options pour la nature basées sur les traductions
        return [
            {
                typeFact: 1,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.1')
            },{
                typeFact: 2,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.2')
            },{
                typeFact: 3,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.3')
            },{
                typeFact: 4,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.4')
            },{
                typeFact: 5,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.5')
            },{
                typeFact: 6,
                libelle: this.translateService.instant('admin.parametres.formulaire.avance.natureOption.6')
            }
        ]
    }
}
